/**
 * Lightning alert configuration constants.
 *
 * Range bounds and option sets MUST stay in sync with:
 *   - Legacy backend validator at `i4cast-workspace/packages/api/src/services/.../validators/validateLightningSlice.ts`
 *   - Nautilus DTO at `apps/nautilus-api/src/modules/events/dto/index.ts` (spec 042)
 *
 * Mirroring: any change here requires the same change on both backends.
 */

export const LIGHTNING_RADIUS_MIN_KM = 1;
export const LIGHTNING_RADIUS_MAX_KM = 100;
export const LIGHTNING_RADIUS_STEP_KM = 1;
export const LIGHTNING_RADIUS_DEFAULT_KM = 10;

export const LIGHTNING_SILENCE_WINDOW_MIN_MIN = 1;
export const LIGHTNING_SILENCE_WINDOW_MIN_MAX = 60;
export const LIGHTNING_SILENCE_WINDOW_MIN_STEP = 1;
export const LIGHTNING_SILENCE_WINDOW_DEFAULT_MIN = 30;

/** Recurrence interval options in hours (when periodicity = 'recurring'). */
export const LIGHTNING_RECURRENCE_HOURS_OPTIONS = [1, 2, 3, 6, 12, 24] as const;
export const LIGHTNING_RECURRENCE_HOURS_DEFAULT = 1;

/** Periodicity values written to the wire as `notificationWindowMin`. */
export const LIGHTNING_NOTIFICATION_WINDOW_SINGLE_MIN = 0;

/** Coverage circle styling (commodity hex; matches spec 042 reference). */
export const LIGHTNING_COVERAGE_FILL_COLOR = "#2cb65f";
export const LIGHTNING_COVERAGE_FILL_OPACITY = 0.32;
export const LIGHTNING_COVERAGE_STROKE_COLOR = "#2cb65f";
export const LIGHTNING_COVERAGE_STROKE_WEIGHT = 1;

export type LightningPeriodicity = "recurring" | "single";
export const LIGHTNING_PERIODICITY_DEFAULT: LightningPeriodicity = "recurring";

/**
 * Convert UI periodicity + recurrenceHours into the wire-format `notificationWindowMin`.
 * - `'single'` → 0 (limit_change semantics on the backend)
 * - `'recurring'` → recurrenceHours * 60 (one of {60, 120, 180, 360, 720, 1440})
 */
export function periodicityToNotificationWindowMin(
    periodicity: LightningPeriodicity,
    recurrenceHours: number,
): number {
    if (periodicity === "single") return LIGHTNING_NOTIFICATION_WINDOW_SINGLE_MIN;
    return recurrenceHours * 60;
}

/**
 * Inverse of `periodicityToNotificationWindowMin`. Used on hydrate.
 * Returns `recurrenceHours = 1` (default) when the wire value is 0 (Único).
 */
export function notificationWindowMinToPeriodicity(
    notificationWindowMin: number,
): { periodicity: LightningPeriodicity; recurrenceHours: number } {
    if (notificationWindowMin === LIGHTNING_NOTIFICATION_WINDOW_SINGLE_MIN) {
        return { periodicity: "single", recurrenceHours: LIGHTNING_RECURRENCE_HOURS_DEFAULT };
    }
    return {
        periodicity: "recurring",
        recurrenceHours: notificationWindowMin / 60,
    };
}
